import parsePhoneNumberFromString, {
  type CountryCode,
  formatIncompletePhoneNumber,
  getCountryCallingCode,
  isSupportedCountry,
} from 'libphonenumber-js/mobile'

import { countryOptions } from '@/helpers/CountriesAndRegions'
import { environment } from '@/helpers/Environment'

export type CountryOption = {
  countryCode: string
  callingCode: string
  label: string
}

export const getAllCountryOptions = (): CountryOption[] =>
  countryOptions
    .filter(({ value }) => isSupportedCountry(value.toUpperCase()))
    .sort((a, b) => a.label.localeCompare(b.label))
    .map(({ label, value }) => {
      const iso2 = value.toUpperCase()
      return {
        countryCode: iso2,
        callingCode: `+${getCountryCallingCode(iso2 as CountryCode)}`,
        label,
      }
    })

// Assuming CurrencyCodes from base.d.ts are currencies we support
const currencyToCountryCodeMap: Record<CurrencyCodes, CountryCode> = {
  AUD: 'AU',
  CAD: 'CA',
  GBP: 'GB',
  NZD: 'NZ',
  USD: 'US',
}

export const getDefaultCountry = (initialPhoneNumberValue: string): CountryCode => {
  if (initialPhoneNumberValue) {
    // If the initial phone number is valid and has a country calling code, use that country
    const parsedPhoneNumber = parsePhoneNumberFromString(initialPhoneNumberValue)
    if (parsedPhoneNumber?.country) {
      return parsedPhoneNumber.country
    }
  }

  const identityFormDefaultCountry = environment.config.custom_identity_fields.country?.default
  if (typeof identityFormDefaultCountry === 'string' && isSupportedCountry(identityFormDefaultCountry.toUpperCase())) {
    return identityFormDefaultCountry.toUpperCase() as CountryCode
  }

  // TODO: get country from venue

  return currencyToCountryCodeMap[environment.portal.default_currency_code] ?? 'US'
}

export const parsePhoneNumber = (
  numberToParse: string,
  selectedCountry: CountryCode,
  defaultCountry: CountryCode,
): { parsedNumber: string; updatedCountry: CountryCode } => {
  const parsedPhoneNumber = parsePhoneNumberFromString(numberToParse, selectedCountry)

  if (!parsedPhoneNumber) return { parsedNumber: numberToParse, updatedCountry: selectedCountry }

  // libphonenumber-js can usually tell apart countries that share the same calling code
  // update selected country based on the value determined from the entered number
  if (parsedPhoneNumber.country) {
    return { parsedNumber: parsedPhoneNumber.number, updatedCountry: parsedPhoneNumber.country }
  }

  // Otherwise prefer default country if possible option
  const possibleCountries = parsedPhoneNumber.getPossibleCountries()
  if (possibleCountries.includes(defaultCountry)) {
    return { parsedNumber: parsedPhoneNumber.number, updatedCountry: defaultCountry }
  }

  return { parsedNumber: parsedPhoneNumber.number, updatedCountry: selectedCountry }
}

export const formatPhoneNumber = (value: string, selectedCountry: CountryCode): string => {
  if (!value) return ''

  const parsedPhoneNumber = parsePhoneNumberFromString(value, selectedCountry)

  if (!parsedPhoneNumber) return value

  // libphonenumber-js can usually tell apart countries that share the same calling code
  // If country isn't yet determined, do not format away the calling code
  if (!parsedPhoneNumber.country) return formatIncompletePhoneNumber(value)

  if (!parsedPhoneNumber.isValid()) return formatIncompletePhoneNumber(value, parsedPhoneNumber.country)

  // Only use formatNational() on valid numbers to avoid bad UX (e.g. 021 188 -> 21188)
  return formatIncompletePhoneNumber(parsedPhoneNumber.formatNational(), parsedPhoneNumber.country)
}
